/**
 * LoadingContext
 *
 * Manages the app-wide loading state and splash screen visibility.
 * Used to ensure critical data and images are ready before showing pages,
 * especially the hero section on the Home page.
 */
import React, { createContext, useContext, useState, useCallback } from 'react';

interface LoadingContextType {
  /** Whether the app-level splash screen should be visible */
  isSplashVisible: boolean;
  /** Show the splash screen */
  showSplash: () => void;
  /** Hide the splash screen */
  hideSplash: () => void;
  /** Current loading progress (0-100) */
  progress: number;
  /** Update the loading progress */
  updateProgress: (value: number) => void;
  /** Start a smooth progress simulation toward a target value */
  startSimulation: (target: number, duration?: number) => void;
  /** Current loading phase string */
  phase: string;
  /** Update the loading phase */
  updatePhase: (value: string) => void;
  /** Whether the progress bar should loop infinitely (repetitive loading) */
  isLooping: boolean;
  /** Set the looping mode */
  setIsLooping: (value: boolean) => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const LoadingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isSplashVisible, setIsSplashVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState('');
  const [isLooping, setIsLooping] = useState(false);
  const simulationInterval = React.useRef<NodeJS.Timeout | null>(null);

  const clearSimulation = useCallback(() => {
    if (simulationInterval.current) {
      clearInterval(simulationInterval.current);
      simulationInterval.current = null;
    }
  }, []);

  const showSplash = useCallback(() => {
    clearSimulation();
    setProgress(0);
    setIsLooping(false);
    setIsSplashVisible(true);
  }, [clearSimulation]);

  const hideSplash = useCallback(() => {
    clearSimulation();
    setIsLooping(false);
    setProgress(100);
    setTimeout(() => {
      setIsSplashVisible(false);
    }, 600);
  }, [clearSimulation]);

  const updateProgress = useCallback((value: number) => {
    setProgress(Math.min(100, Math.max(0, value)));
  }, []);

  /**
   * Recursive simulation that moves the bar toward a target
   * but slows down as it approaches to avoid hitting it too early.
   */
  const startSimulation = useCallback((target: number, duration: number = 2000) => {
    clearSimulation();
    
    const startTime = Date.now();
    const startValue = progress;
    const range = target - startValue;

    if (range <= 0) return;

    simulationInterval.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const t = Math.min(1, elapsed / duration);
      
      // Easing function for "perceived speed" (easeOutExpo-ish)
      const currentProgress = startValue + range * (1 - Math.pow(2, -10 * t));
      
      setProgress(Math.min(target, currentProgress));
      
      if (t >= 1) clearSimulation();
    }, 50);
  }, [progress, clearSimulation]);

  const updatePhase = useCallback((value: string) => {
    setPhase(value);
  }, []);

  return (
    <LoadingContext.Provider
      value={{
        isSplashVisible,
        showSplash,
        hideSplash,
        progress,
        updateProgress,
        startSimulation,
        phase,
        updatePhase,
        isLooping,
        setIsLooping
      }}
    >
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};
